import {
  infiniteQueryOptions,
  queryOptions,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query';

import { fetchLibraryPostsGateway } from './fetchLibraryPostsGateway';
import { expandsCancerTypeSubtree } from '../model/listParams';
import type {
  LibraryListParams,
  LibraryPostPage,
  LibraryPostParams,
  LibraryPostSummary,
  SharedLibraryPost,
} from '../model/types';
import type { LibraryPostsGateway } from '../ports/libraryPostsGateway';

import { FIVE_MINUTES, ONE_HOUR } from '@/constants/time';
import { userScoped } from '@/util/userScope';

/**
 * The library's queries. Every key here is pinned by `queryKeys.test.ts`:
 * code across the app reads and patches these caches by key and by the
 * `communityPostsV2` prefix, so a key is a contract, not a detail.
 *
 * Each factory takes the gateway to read through, defaulting to the real one,
 * so a hook can pass the one its provider holds.
 */

export const getCommunityPostsV2InfiniteQueryOptions = userScoped(
  'viewer',
  function getCommunityPostsV2InfiniteQueryOptions(
    userId: string | undefined,
    params?: LibraryListParams,
    gateway: LibraryPostsGateway = fetchLibraryPostsGateway
  ) {
    const { limit, cancerTypeId, q, saved, sortBy } = params || {};

    const includeDescendants = expandsCancerTypeSubtree(params ?? {}) || undefined;

    return infiniteQueryOptions({
      queryKey: [
        'communityPostsV2',
        userId,
        { cancerTypeId, includeDescendants, q, saved, sortBy },
      ],
      queryFn: ({ pageParam }) =>
        gateway.listPosts({
          page: pageParam,
          limit,
          cancerTypeId,
          includeDescendants,
          q,
          saved,
          sortBy,
        }),
      initialPageParam: 1,
      getNextPageParam: (lastPage, _allPages, lastPageParam) => {
        if (!lastPage || lastPage.posts.length < (lastPage.limit ?? 25)) {
          return undefined;
        }

        return lastPageParam + 1;
      },
    });
  }
);

/**
 * One page of the library, as a cached query rather than a bare fetch.
 *
 * Adjacent-post navigation needs the page a post sits on to know what comes
 * before and after it. Fetching that directly meant every post view spent
 * another request on a page the reader had usually just loaded, which is a real
 * cost against the rate limit on the list endpoint. Keyed by page so the 25
 * posts on it resolve one fetch between them.
 */
export const getCommunityPostsV2PageQueryOptions = userScoped(
  'viewer',
  function getCommunityPostsV2PageQueryOptions(
    userId: string | undefined,
    params: LibraryListParams,
    gateway: LibraryPostsGateway = fetchLibraryPostsGateway
  ) {
    const { page = 1, limit = 25, cancerTypeId, q, saved, sortBy } = params;

    const includeDescendants = expandsCancerTypeSubtree(params) || undefined;

    return queryOptions({
      queryKey: [
        'communityPostsV2',
        userId,
        'page',
        { page, limit, cancerTypeId, includeDescendants, q, saved, sortBy },
      ],
      queryFn: () =>
        gateway.listPosts({ page, limit, cancerTypeId, includeDescendants, q, saved, sortBy }),
      staleTime: FIVE_MINUTES,
      gcTime: FIVE_MINUTES,
    });
  }
);

export const getCommunityPostV2QueryOptions = userScoped(
  'viewer',
  function getCommunityPostV2QueryOptions(
    userId: string | undefined,
    { postId, q }: LibraryPostParams,
    gateway: LibraryPostsGateway = fetchLibraryPostsGateway
  ) {
    return queryOptions({
      queryKey: ['communityPostsV2', userId, postId, { q }],
      queryFn: () => gateway.getPost(q ? { postId, q } : { postId }, { userId }),
      staleTime: ONE_HOUR,
      gcTime: ONE_HOUR,
    });
  }
);

export function getPublicCommunityPostV2QueryOptions(
  { token }: { token: string },
  gateway: LibraryPostsGateway = fetchLibraryPostsGateway
) {
  return queryOptions<SharedLibraryPost>({
    queryKey: ['publicCommunityPost', token],
    queryFn: () => gateway.getSharedPost(token),
    staleTime: ONE_HOUR,
    gcTime: ONE_HOUR,
    enabled: !!token,
    retry: false,
  });
}

/**
 * A post as the library list already holds it, if any cached list page has it.
 *
 * Lets the post screen shape its loading state from what the reader just
 * tapped, whether it carries a summary and how many comments, instead of
 * guessing and jumping when the real post lands. Reads both the list's infinite
 * query and the per-page queries adjacent navigation keeps. Returns `undefined`
 * for a post reached by a deep link, which never passed through the list.
 */
export function findListedCommunityPost(
  queryClient: QueryClient,
  userId: string | undefined,
  postId: number
): LibraryPostSummary | undefined {
  const cached = queryClient.getQueriesData<unknown>({ queryKey: ['communityPostsV2', userId] });

  for (const [, data] of cached) {
    const pages =
      data && typeof data === 'object' && 'pages' in data
        ? (data as InfiniteData<LibraryPostPage>).pages
        : [data as LibraryPostPage | undefined];

    for (const page of pages) {
      const match = page?.posts?.find((post) => post.id === postId);

      if (match) {
        return match;
      }
    }
  }

  return undefined;
}
